import { useEffect, useState } from "react";
import CoachLayout from "@/components/CoachLayout";
import api from "@/lib/api";
import { Loader2, Plus, Trash2, Ticket, Power } from "lucide-react";
import { toast } from "sonner";

export default function CoachPromo() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: "", scope: "both", max_redemptions: "", expires_at: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setPromos(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim() || !form.value) return toast.error("Code et valeur requis");
    setSaving(true);
    try {
      await api.post("/admin/promo-codes", {
        code: form.code, type: form.type, value: parseFloat(form.value), scope: form.scope,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions) : null,
        expires_at: form.expires_at || null,
      });
      setForm({ code: "", type: "percent", value: "", scope: "both", max_redemptions: "", expires_at: "" });
      load(); toast.success("Code promo créé");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const toggle = async (code, active) => { await api.patch(`/admin/promo-codes/${code}`, { active: !active }); load(); };
  const remove = async (code) => { await api.delete(`/admin/promo-codes/${code}`); load(); toast.success("Supprimé"); };

  return (
    <CoachLayout>
      <div className="fade-up">
        <h1 className="text-2xl font-medium flex items-center gap-2"><Ticket size={20} strokeWidth={1.6} /> Codes promo</h1>
        <p className="mt-2 text-sm text-obsidian/50">Crée et gère les codes de réduction appliqués au paiement Stripe.</p>

        <div className="mt-6 bg-white border border-border p-6 max-w-3xl">
          <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-4">Nouveau code</div>
          <div className="grid sm:grid-cols-6 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-obsidian/50">Code</label>
              <input data-testid="promo-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm uppercase focus:ring-1 focus:ring-obsidian focus:outline-none" placeholder="RUN20" />
            </div>
            <div>
              <label className="text-xs text-obsidian/50">Type</label>
              <select data-testid="promo-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm bg-white focus:ring-1 focus:ring-obsidian focus:outline-none">
                <option value="percent">%</option>
                <option value="amount">€</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-obsidian/50">Valeur</label>
              <input data-testid="promo-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm tabnum focus:ring-1 focus:ring-obsidian focus:outline-none" placeholder="20" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-obsidian/50">S'applique à</label>
              <select data-testid="promo-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm bg-white focus:ring-1 focus:ring-obsidian focus:outline-none">
                <option value="both">Les deux (cycle + programme complet)</option>
                <option value="cycle">Un seul cycle</option>
                <option value="full">Programme complet</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-obsidian/50">Max util. (opt.)</label>
              <input type="number" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                className="mt-1 w-full rounded-sm border border-border px-3 py-2.5 text-sm tabnum focus:ring-1 focus:ring-obsidian focus:outline-none" placeholder="∞" />
            </div>
          </div>
          <button data-testid="promo-create" onClick={create} disabled={saving}
            className="mt-4 rounded-sm bg-obsidian text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Créer le code
          </button>
        </div>

        <div className="mt-8 overflow-x-auto no-scrollbar -mx-4 sm:mx-0 px-4 sm:px-0">
         <div className="min-w-[720px] border-t border-l border-border">
          <div className="grid grid-cols-12 bg-obsidian text-white text-[11px] tracking-[0.14em] uppercase">
            <div className="col-span-3 px-4 py-3 border-r border-white/10">Code</div>
            <div className="col-span-2 px-4 py-3 border-r border-white/10">Réduction</div>
            <div className="col-span-2 px-4 py-3 border-r border-white/10">Portée</div>
            <div className="col-span-2 px-4 py-3 border-r border-white/10">Utilisations</div>
            <div className="col-span-1 px-4 py-3 border-r border-white/10">Statut</div>
            <div className="col-span-2 px-4 py-3">Actions</div>
          </div>
          {promos.length === 0 && <div className="bg-white border-b border-r border-border px-4 py-5 text-sm text-obsidian/40">Aucun code pour le moment.</div>}
          {promos.map((p) => (
            <div key={p.code} data-testid={`promo-row-${p.code}`} className="grid grid-cols-12 items-center bg-white border-b border-r border-border text-sm">
              <div className="col-span-3 px-4 py-3 border-r border-border font-mono font-medium">{p.code}</div>
              <div className="col-span-2 px-4 py-3 border-r border-border">{p.type === "percent" ? `${p.value}%` : `${p.value} €`}</div>
              <div className="col-span-2 px-4 py-3 border-r border-border text-xs text-obsidian/70" data-testid={`promo-scope-${p.code}`}>
                {p.scope === "cycle" ? "Un cycle" : p.scope === "full" ? "Programme complet" : "Cycle + complet"}
              </div>
              <div className="col-span-2 px-4 py-3 border-r border-border tabnum">{p.redemptions || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</div>
              <div className="col-span-1 px-4 py-3 border-r border-border">
                <span className={`text-xs ${p.active ? "text-[#4A6B53]" : "text-obsidian/40"}`}>{p.active ? "actif" : "inactif"}</span>
              </div>
              <div className="col-span-2 px-4 py-3 flex gap-2">
                <button data-testid={`promo-toggle-${p.code}`} onClick={() => toggle(p.code, p.active)} className="rounded-sm border border-border px-3 py-1.5 text-xs inline-flex items-center gap-1.5"><Power size={13} /> {p.active ? "Désactiver" : "Activer"}</button>
                <button data-testid={`promo-delete-${p.code}`} onClick={() => remove(p.code)} className="rounded-sm border border-border px-3 py-1.5 text-xs inline-flex items-center gap-1.5 text-destructive"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
         </div>
        </div>
      </div>
    </CoachLayout>
  );
}
